/**
 * Custom jewelry CTA section — gallery auto-scroll (horizontal or vertical by overflow).
 * Idempotent: safe to load multiple times per page.
 *
 * Performance notes:
 * - Bounds (axis + maxScroll) are cached and only recomputed via ResizeObserver
 *   to avoid layout thrash inside the RAF tick.
 * - Each tick performs a single `scrollLeft`/`scrollTop` read and at most one
 *   write so the browser never flushes layout mid-frame.
 * - Auto-scroll pauses while the gallery is offscreen via IntersectionObserver.
 * - After a touch/flick we wait for scrolling to actually settle (not a fixed
 *   timer) before resuming, so JS never fights iOS inertial momentum.
 */
(function () {
  const BOOT_KEY = '__jewelryCtaGalleryAutoscrollBoot';
  const API_KEY = 'jewelryCtaGalleryAutoscroll';

  const mqReduce =
    typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  const mounted = new WeakMap();

  function isMobileHorizontal(axis) {
    return axis === 'x' && window.matchMedia && window.matchMedia('(max-width: 740px)').matches;
  }

  function effectiveSpeed(baseSpeed, axis) {
    if (!isMobileHorizontal(axis)) return baseSpeed;
    return Math.max(56, baseSpeed * 1.6);
  }

  function now() {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  function mount(el) {
    if (!el || el.getAttribute('data-jewelry-cta-autoscroll') !== 'true' || mounted.has(el)) return;

    const parsed = parseFloat(el.getAttribute('data-jewelry-cta-speed'));
    const speed = Number.isFinite(parsed) ? Math.max(0, parsed) : 36;

    const state = {
      el,
      paused: false,
      inView: true,
      userInteracting: false,
      autoScrollWrite: false,
      raf: 0,
      then: now(),
      resumeTimer: null,
      speed,
      ro: null,
      io: null,
      onMq: null,
      halt: null,
      warmResume: null,
      delayedResume: null,
      onScroll: null,
      onTouchStart: null,
      onTouchEnd: null,
      boundTick: null,
      direction: 1,
      axis: null,
      maxScroll: 0,
    };

    function recomputeBounds() {
      const ew = el.scrollWidth - el.clientWidth;
      const ns = el.scrollHeight - el.clientHeight;
      if (ew <= 4 && ns <= 4) {
        state.axis = null;
        state.maxScroll = 0;
      } else if (ns >= ew && ns > 4) {
        state.axis = 'y';
        state.maxScroll = ns;
      } else if (ew > 4) {
        state.axis = 'x';
        state.maxScroll = ew;
      } else {
        state.axis = 'y';
        state.maxScroll = ns;
      }
    }

    function pauseLoop() {
      if (state.raf) cancelAnimationFrame(state.raf);
      state.raf = 0;
    }

    function runningAllowed() {
      return !mqReduce.matches && !state.paused && state.inView && state.speed > 0;
    }

    function startLoopIfEligible() {
      if (!runningAllowed()) {
        pauseLoop();
        return;
      }
      if (!state.raf) {
        state.then = now();
        state.raf = requestAnimationFrame(state.boundTick);
      }
    }

    state.boundTick = function tick() {
      state.raf = 0;
      if (!runningAllowed()) return;

      const t = now();
      let dt = (t - state.then) / 1000;
      if (dt > 0.2) dt = 0.2;
      state.then = t;

      if (!state.axis || state.maxScroll <= 4) {
        state.raf = requestAnimationFrame(state.boundTick);
        return;
      }

      if (state.axis === 'x') {
        const cur = el.scrollLeft;
        let next = cur + effectiveSpeed(state.speed, 'x') * dt * state.direction;
        if (next >= state.maxScroll - 2) {
          next = state.maxScroll;
          state.direction = -1;
        } else if (next <= 2) {
          next = 0;
          state.direction = 1;
        }
        if (next !== cur) {
          state.autoScrollWrite = true;
          el.scrollLeft = next;
        }
      } else {
        const cur = el.scrollTop;
        let next = cur + state.speed * dt;
        if (next >= state.maxScroll - 2) next = 0;
        if (next !== cur) {
          state.autoScrollWrite = true;
          el.scrollTop = next;
        }
      }

      state.raf = requestAnimationFrame(state.boundTick);
    };

    state.halt = function () {
      state.paused = true;
      if (state.resumeTimer) {
        clearTimeout(state.resumeTimer);
        state.resumeTimer = null;
      }
      pauseLoop();
    };

    state.warmResume = function () {
      if (state.userInteracting) return;
      state.paused = false;
      state.then = now();
      startLoopIfEligible();
    };

    state.delayedResume = function () {
      if (state.resumeTimer) clearTimeout(state.resumeTimer);
      state.resumeTimer = setTimeout(state.warmResume, 1200);
    };

    // While paused, scroll events come from the user's flick / iOS inertial
    // momentum. Keep pushing the resume timer back so the auto loop only
    // restarts after scrolling has truly settled.
    state.onScroll = function () {
      if (state.autoScrollWrite) {
        state.autoScrollWrite = false;
        return;
      }
      if (!state.paused) return;
      if (state.resumeTimer) clearTimeout(state.resumeTimer);
      state.resumeTimer = setTimeout(state.warmResume, 700);
    };

    state.onTouchStart = function () {
      state.userInteracting = true;
      state.halt();
    };

    state.onTouchEnd = function () {
      state.userInteracting = false;
      state.delayedResume();
    };

    state.onMq = function () {
      startLoopIfEligible();
    };

    if (mqReduce.addEventListener) mqReduce.addEventListener('change', state.onMq);

    el.addEventListener('mouseenter', state.halt);
    el.addEventListener('mouseleave', state.warmResume);
    el.addEventListener('touchstart', state.onTouchStart, { passive: true });
    el.addEventListener('touchend', state.onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', state.onTouchEnd, { passive: true });
    el.addEventListener('focusin', state.halt);
    el.addEventListener('focusout', state.warmResume);
    el.addEventListener('scroll', state.onScroll, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      state.ro = new ResizeObserver(function () {
        recomputeBounds();
        startLoopIfEligible();
      });
      state.ro.observe(el);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      state.io = new IntersectionObserver(
        function (entries) {
          for (let i = 0; i < entries.length; i++) {
            state.inView = entries[i].isIntersecting;
          }
          startLoopIfEligible();
        },
        { rootMargin: '200px' }
      );
      state.io.observe(el);
    }

    recomputeBounds();

    function cleanup() {
      pauseLoop();
      if (state.resumeTimer) clearTimeout(state.resumeTimer);
      if (state.ro) {
        state.ro.disconnect();
        state.ro = null;
      }
      if (state.io) {
        state.io.disconnect();
        state.io = null;
      }
      if (mqReduce.removeEventListener && state.onMq) mqReduce.removeEventListener('change', state.onMq);
      el.removeEventListener('mouseenter', state.halt);
      el.removeEventListener('mouseleave', state.warmResume);
      el.removeEventListener('touchstart', state.onTouchStart);
      el.removeEventListener('touchend', state.onTouchEnd);
      el.removeEventListener('touchcancel', state.onTouchEnd);
      el.removeEventListener('focusin', state.halt);
      el.removeEventListener('focusout', state.warmResume);
      el.removeEventListener('scroll', state.onScroll);
      mounted.delete(el);
    }

    mounted.set(el, cleanup);
    startLoopIfEligible();
  }

  function unmount(el) {
    const cleanup = mounted.get(el);
    if (typeof cleanup === 'function') cleanup();
  }

  function mountAll(root) {
    const scope = root && root.querySelector ? root : document;
    scope.querySelectorAll('.custom-jewelry-cta__gallery[data-jewelry-cta-autoscroll="true"]').forEach(mount);
  }

  function mountBySectionId(sectionId) {
    if (!sectionId) return;
    const root = document.getElementById('shopify-section-' + sectionId);
    if (!root) return;
    const el = root.querySelector('.custom-jewelry-cta__gallery[data-jewelry-cta-autoscroll="true"]');
    if (el) mount(el);
  }

  if (!window[API_KEY]) {
    window[API_KEY] = {
      mount: mount,
      unmount: unmount,
      mountAll: mountAll,
      mountBySectionId: mountBySectionId,
    };
  }

  if (!window[BOOT_KEY]) {
    window[BOOT_KEY] = true;
    const API = window[API_KEY];

    document.addEventListener('shopify:section:load', function (e) {
      if (e && e.target) API.mountAll(e.target);
    });

    document.addEventListener('shopify:section:unload', function (e) {
      if (e && e.target) {
        e.target.querySelectorAll('.custom-jewelry-cta__gallery[data-jewelry-cta-autoscroll="true"]').forEach(unmount);
      }
    });

    function boot() {
      API.mountAll(document);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
})();
